import {
    BATCH_BYTES,
    BATCH_EVENTS,
    FOREIGN_OWNER_TTL_MS,
    GUEST_CAP,
    GUEST_MAX_AGE_MS,
    ORPHAN_STALE_MS,
    OUTBOX_PREFIX,
    SIGNED_CAP,
    TOTAL_BYTES_CAP,
} from "./config";
import type { ActivityEvent, OutboxEntry, OutboxRecord, SessionHeader } from "./types";

const SID_RE = /^\d{13}-[0-9a-f]{16}$/;

const defaultStore = (): Storage | null => {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

const isQuotaError = (e: unknown): boolean =>
    e instanceof DOMException &&
    (e.name === "QuotaExceededError" || e.name === "NS_ERROR_DOM_QUOTA_REACHED" || e.code === 22);

const parseRecord = (raw: string | null): OutboxRecord | null => {
    if (!raw) return null;
    const r = JSON.parse(raw) as Partial<OutboxRecord> | null;
    if (!r || r.v !== 1 || typeof r.sid !== "string" || !SID_RE.test(r.sid)) return null;
    if (r.owner !== null && typeof r.owner !== "string") return null;
    if (!r.header || typeof r.header !== "object" || r.header.id !== r.sid) return null;
    const events = Array.isArray(r.events)
        ? r.events.filter(
              (e): e is OutboxEntry =>
                  !!e &&
                  typeof e.b === "number" &&
                  !!e.ev &&
                  typeof e.ev.seq === "number" &&
                  typeof e.ev.type === "string",
          )
        : [];
    return {
        v: 1,
        sid: r.sid,
        owner: r.owner ?? null,
        part: Number.isInteger(r.part) && (r.part as number) >= 1 ? (r.part as number) : 1,
        tab_id: typeof r.tab_id === "string" ? r.tab_id : "",
        header: r.header as SessionHeader,
        events,
        dropped: typeof r.dropped === "number" ? r.dropped : 0,
        updated_at: typeof r.updated_at === "number" ? r.updated_at : 0,
    };
};

export interface AdoptContext {
    email: string | null;
    tabId: string;
    prevSessionId?: string;
    /**
     * Гостевые очереди этой вкладки: "adopt" — пока гость или вход прямо здесь,
     * "drop" — вход случился не в этой вкладке, чужие для него действия стираем.
     */
    guests: "adopt" | "drop";
}

/**
 * Очередь неотправленных событий: по ключу localStorage на сеанс. Истина — в
 * памяти; в хранилище пишем не чаще раза в секунду и сразу при уходе со
 * страницы, чтобы очередь пережила перезагрузку и возврат из Google/Stripe.
 */
export class Outbox {
    readonly records = new Map<string, OutboxRecord>();
    private dirty = new Set<string>();
    private timer: ReturnType<typeof setTimeout> | null = null;
    private total = 0;

    constructor(
        private readonly now: () => number = Date.now,
        private readonly store: () => Storage | null = defaultStore,
    ) {}

    create(header: SessionHeader, owner: string | null, tabId: string): OutboxRecord {
        const rec: OutboxRecord = {
            v: 1,
            sid: header.id,
            owner,
            part: 1,
            tab_id: tabId,
            header,
            events: [],
            dropped: 0,
            updated_at: this.now(),
        };
        this.records.set(rec.sid, rec);
        return rec;
    }

    get(sid: string): OutboxRecord | undefined {
        return this.records.get(sid);
    }

    totalBytes(): number {
        return this.total;
    }

    push(sid: string, ev: ActivityEvent, bytes: number): void {
        const rec = this.records.get(sid);
        if (!rec) return;
        rec.events.push({ ev, b: bytes });
        this.total += bytes;
        const cap = rec.owner ? SIGNED_CAP : GUEST_CAP;
        while (rec.events.length > cap) this.dropOldest(rec);
        while (this.total > TOTAL_BYTES_CAP) {
            const victim = this.oldestWithEvents();
            if (!victim) break;
            this.dropOldest(victim);
        }
        this.markDirty(sid);
    }

    pendingFor(owner: string | null): number {
        let n = 0;
        this.records.forEach((r) => {
            if (r.owner === owner) n += r.events.length;
        });
        return n;
    }

    /** Сеансы владельца с событиями — старые первыми. */
    sendable(owner: string): OutboxRecord[] {
        return Array.from(this.records.values())
            .filter((r) => r.owner === owner && r.events.length > 0)
            .sort((a, b) => a.header.started_at_ms - b.header.started_at_ms);
    }

    takeBatch(
        rec: OutboxRecord,
        maxEvents: number = BATCH_EVENTS,
        maxBytes: number = BATCH_BYTES,
    ): OutboxEntry[] {
        const out: OutboxEntry[] = [];
        let bytes = 0;
        for (const e of rec.events) {
            if (out.length >= maxEvents) break;
            if (out.length > 0 && bytes + e.b > maxBytes) break;
            out.push(e);
            bytes += e.b;
        }
        return out;
    }

    /** Сервер подтвердил всё до seq включительно. */
    ack(sid: string, throughSeq: number): void {
        const rec = this.records.get(sid);
        if (!rec) return;
        while (rec.events.length > 0 && rec.events[0].ev.seq <= throughSeq) {
            this.total -= rec.events.shift()!.b;
        }
        this.markDirty(sid);
    }

    /** Выбросить конкретные события (сервер их не примет никогда). */
    drop(sid: string, seqs: ReadonlySet<number>, countAsDropped: boolean): void {
        const rec = this.records.get(sid);
        if (!rec) return;
        const keep: OutboxEntry[] = [];
        for (const e of rec.events) {
            if (seqs.has(e.ev.seq)) {
                this.total -= e.b;
                if (countAsDropped) rec.dropped++;
            } else keep.push(e);
        }
        rec.events = keep;
        this.markDirty(sid);
    }

    /** Документ на сервере заполнен — остаток идёт в следующую часть. */
    nextPart(sid: string): void {
        const rec = this.records.get(sid);
        if (!rec) return;
        rec.part += 1;
        this.markDirty(sid);
    }

    dropRecord(sid: string): void {
        const rec = this.records.get(sid);
        if (!rec) return;
        rec.events.forEach((e) => (this.total -= e.b));
        this.records.delete(sid);
        this.markDirty(sid);
    }

    /**
     * Гость вошёл: его сеансы (текущий и подобранные из этой же вкладки)
     * переходят к владельцу; события старше двух часов не отправляются.
     * Возвращает, сколько гостевых событий уйдёт с пометкой pre_auth.
     */
    assignOwner(email: string, maxAgeMs: number = GUEST_MAX_AGE_MS): number {
        const cutoff = this.now() - maxAgeMs;
        let kept = 0;
        this.records.forEach((rec) => {
            if (rec.owner !== null) return;
            rec.owner = email;
            const keep: OutboxEntry[] = [];
            for (const e of rec.events) {
                const t = Date.parse(e.ev.t_utc);
                if (Number.isFinite(t) && t >= cutoff) keep.push(e);
                else {
                    this.total -= e.b;
                    rec.dropped++;
                }
            }
            rec.events = keep;
            kept += keep.filter((e) => e.ev.pre_auth).length;
            this.markDirty(rec.sid);
        });
        return kept;
    }

    /** Гостевые очереди в памяти — вход случился в другой вкладке. */
    dropGuests(): void {
        Array.from(this.records.values())
            .filter((r) => r.owner === null)
            .forEach((r) => this.dropRecord(r.sid));
    }

    /**
     * Подобрать очереди прошлых экземпляров страницы.
     * - свои (владелец совпадает): если ключ не трогали минуту или это прошлый
     *   сеанс этой же вкладки; живая вкладка может отправить их повторно, но
     *   сервер отбрасывает повторы по seq;
     * - гостевые: только из этой же вкладки и не старше двух часов;
     * - чужие не трогаем, пока не истекут.
     */
    adoptOrphans(ctx: AdoptContext): number {
        const st = this.store();
        if (!st) return 0;
        const now = this.now();
        const keys: string[] = [];
        try {
            for (let i = 0; i < st.length; i++) {
                const k = st.key(i);
                if (k && k.startsWith(OUTBOX_PREFIX)) keys.push(k);
            }
        } catch {
            return 0;
        }
        let adopted = 0;
        for (const key of keys) {
            const sid = key.slice(OUTBOX_PREFIX.length);
            if (this.records.has(sid)) continue;
            let rec: OutboxRecord | null = null;
            try {
                rec = parseRecord(st.getItem(key));
            } catch {
                rec = null;
            }
            if (!rec || rec.sid !== sid || rec.events.length === 0) {
                this.removeKey(st, key);
                continue;
            }
            const age = now - rec.updated_at;
            if (rec.owner === null) {
                if (age > GUEST_MAX_AGE_MS) {
                    this.removeKey(st, key);
                    continue;
                }
                if (!rec.tab_id || rec.tab_id !== ctx.tabId) continue;
                if (ctx.guests === "drop") {
                    this.removeKey(st, key);
                    continue;
                }
            } else if (rec.owner !== ctx.email) {
                if (age > FOREIGN_OWNER_TTL_MS) this.removeKey(st, key);
                continue;
            } else if (age < ORPHAN_STALE_MS && sid !== ctx.prevSessionId) {
                continue;
            }
            this.records.set(sid, rec);
            rec.events.forEach((e) => (this.total += e.b));
            adopted++;
        }
        return adopted;
    }

    /** Удаление аккаунта: его очередь не должна уйти ни сейчас, ни потом. */
    purgeOwner(email: string): void {
        Array.from(this.records.values())
            .filter((r) => r.owner === email)
            .forEach((r) => this.dropRecord(r.sid));
        const st = this.store();
        if (!st) return;
        const keys: string[] = [];
        try {
            for (let i = 0; i < st.length; i++) {
                const k = st.key(i);
                if (k && k.startsWith(OUTBOX_PREFIX)) keys.push(k);
            }
            for (const k of keys) {
                let owner: string | null = null;
                try {
                    owner = parseRecord(st.getItem(k))?.owner ?? null;
                } catch {
                    owner = null;
                }
                if (owner === email) this.removeKey(st, k);
            }
        } catch {
            /* best effort */
        }
        this.persistNow();
    }

    persistSoon(): void {
        if (this.timer || this.dirty.size === 0) return;
        this.timer = setTimeout(() => {
            this.timer = null;
            const w = window as Window & {
                requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
            };
            if (typeof w.requestIdleCallback === "function") {
                w.requestIdleCallback(() => this.persistNow(), { timeout: 1_000 });
            } else this.persistNow();
        }, 1_000);
    }

    persistNow(): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        const st = this.store();
        if (!st || this.dirty.size === 0) return;
        const sids = Array.from(this.dirty);
        this.dirty.clear();
        for (const sid of sids) this.writeOne(st, sid);
    }

    dump(): OutboxRecord[] {
        return Array.from(this.records.values());
    }

    private writeOne(st: Storage, sid: string): void {
        const key = OUTBOX_PREFIX + sid;
        const rec = this.records.get(sid);
        if (!rec || rec.events.length === 0) {
            this.removeKey(st, key);
            return;
        }
        for (let attempt = 0; attempt < 12; attempt++) {
            rec.updated_at = this.now();
            try {
                st.setItem(key, JSON.stringify(rec));
                return;
            } catch (e) {
                if (!isQuotaError(e)) return;
                // Место кончилось: сначала чужие очереди, старые первыми, потом
                // старшая половина своей.
                if (this.removeOldestForeign(st)) continue;
                const half = Math.max(1, Math.floor(rec.events.length / 2));
                for (let i = 0; i < half; i++) this.dropOldest(rec);
                if (rec.events.length === 0) {
                    this.removeKey(st, key);
                    return;
                }
            }
        }
    }

    private removeOldestForeign(st: Storage): boolean {
        let oldest: { key: string; at: number } | null = null;
        try {
            for (let i = 0; i < st.length; i++) {
                const k = st.key(i);
                if (!k || !k.startsWith(OUTBOX_PREFIX)) continue;
                if (this.records.has(k.slice(OUTBOX_PREFIX.length))) continue;
                let at = 0;
                try {
                    at = parseRecord(st.getItem(k))?.updated_at ?? 0;
                } catch {
                    at = 0;
                }
                if (!oldest || at < oldest.at) oldest = { key: k, at };
            }
        } catch {
            return false;
        }
        if (!oldest) return false;
        this.removeKey(st, oldest.key);
        return true;
    }

    private removeKey(st: Storage, key: string): void {
        try {
            st.removeItem(key);
        } catch {
            /* best effort */
        }
    }

    private dropOldest(rec: OutboxRecord): void {
        const e = rec.events.shift();
        if (!e) return;
        this.total -= e.b;
        rec.dropped++;
        this.markDirty(rec.sid);
    }

    private oldestWithEvents(): OutboxRecord | undefined {
        let best: OutboxRecord | undefined;
        this.records.forEach((r) => {
            if (r.events.length === 0) return;
            if (!best || r.events[0].ev.t_utc < best.events[0].ev.t_utc) best = r;
        });
        return best;
    }

    private markDirty(sid: string): void {
        this.dirty.add(sid);
        this.persistSoon();
    }
}
